// SVG shape generators (organic blob, section wave). Pure path math.

export type SvgGenVariant = 'svg-blob-generator' | 'svg-wave-generator';

type Point = [number, number];

// Catmull-Rom spline through a closed set of points, emitted as cubic beziers
// so the resulting blob is smooth and organic.
function catmullRomClosed(points: Point[]): string {
  const n = points.length;
  if (n < 3) return '';
  let d = `M ${points[0][0].toFixed(1)} ${points[0][1].toFixed(1)}`;
  for (let i = 0; i < n; i += 1) {
    const p0 = points[(i - 1 + n) % n];
    const p1 = points[i];
    const p2 = points[(i + 1) % n];
    const p3 = points[(i + 2) % n];
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`;
  }
  return `${d} Z`;
}

export function blobPath(radii: number[], size = 500, contrast = 0.4): string {
  const cx = size / 2;
  const cy = size / 2;
  const base = size * 0.38;
  const n = radii.length;
  const points: Point[] = radii.map((r, i) => {
    const angle = (i / n) * Math.PI * 2;
    const radius = base * (1 - contrast + contrast * r);
    return [cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius];
  });
  return catmullRomClosed(points);
}

export function wavePath(heights: number[], width = 1440, height = 320, amplitude = 0.6): string {
  const n = heights.length;
  const step = width / (n - 1);
  const baseY = height * 0.5;
  const amp = height * 0.4 * amplitude;
  const points = heights.map((h, i): Point => [i * step, baseY + (h * 2 - 1) * amp]);
  let d = `M 0 ${height} L ${points[0][0].toFixed(1)} ${points[0][1].toFixed(1)}`;
  for (let i = 0; i < n - 1; i += 1) {
    const p1 = points[i];
    const p2 = points[i + 1];
    const midX = (p1[0] + p2[0]) / 2;
    d += ` C ${midX.toFixed(1)} ${p1[1].toFixed(1)}, ${midX.toFixed(1)} ${p2[1].toFixed(1)}, ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`;
  }
  d += ` L ${width} ${height} Z`;
  return d;
}

export function blobSvg(path: string, color: string, size = 500): string {
  return `<svg viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">\n  <path fill="${color}" d="${path}" />\n</svg>`;
}

export function waveSvg(path: string, color: string, width = 1440, height = 320): string {
  return `<svg viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">\n  <path fill="${color}" d="${path}" />\n</svg>`;
}
